import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createBookingRequest, createTryonDownloadTicket, createTryonSession, deleteSavedTryon, downloadTryon, getArtists, getBootstrap, getDesigns, getSavedTryons,
  getTryonPreview, saveTryon, track, uploadTryonAssets,
} from './api/client';
import {
  asDesignAssetError, designAssetDiagnostic, invalidateCatalogAsset, isDesignAssetCancellation, staleDesignAssetError,
} from './api/assets';
import type { Artist, BootstrapResponse, SavedTryon } from './api/client';
import type { TryonSyncState } from './api/client';
import { featureFlags } from './config/brand';
import { tattooDesigns } from './config/designs';
import type { TattooDesign } from './config/designs';
import type { CanvasEngine } from './editor/CanvasEngine';
import { DesignSelectionGuard } from './editor/designSelection';
import type { EditorState } from './editor/types';
import { StudioAdmin } from './features/admin/StudioAdmin';
import type { AdminTab } from './features/admin/StudioAdmin';
import {
  ArtistProfileScreen, ArtistSelectScreen, BookingDateScreen, BookingLocationScreen, BookingReviewScreen, BookingSuccessScreen,
  BookingTimeScreen, TryonDetailScreen,
} from './features/booking/BookingScreens';
import type { BookingDraft } from './features/booking/BookingScreens';
import { OfferScreen } from './features/b2b/OfferScreen';
import { B2BRequestScreen } from './features/b2b/B2BRequestScreen';
import { DesignLibrary } from './features/design/DesignLibrary';
import type { CustomDesign } from './features/design/DesignLibrary';
import { EditorScreen } from './features/editor/EditorScreen';
import { HomeScreen } from './features/home/HomeScreen';
import type { HomeTryon } from './features/home/HomeScreen';
import { sortHomeTryonsNewestFirst } from './features/home/tryonOrder';
import { ResultScreen } from './features/result/ResultScreen';
import { RevealScreen } from './features/reveal/RevealScreen';
import { PrivacyScreen } from './features/privacy/PrivacyPanel';
import { canOpenAdmin, resolveDeepRoute, roleLanding } from './navigation/appNavigation';
import type { AppRoute } from './navigation/appNavigation';
import {
  deleteStoredResult, getStoredResult, listStoredResults, storeResult, updateStoredResult,
} from './storage/results';
import type { StoredResult } from './storage/results';
import type { StorageScope } from './storage/results';
import { runtime as telegram } from './runtime/runtime';
import { localLocalePreference, useI18n } from './i18n/I18nProvider';
import { ConfirmDialog } from './components/ConfirmDialog';
import { installRuntimeDiagnostics } from './diagnostics/runtimeDiagnostics';
import { clientRuntime } from './diagnostics/runtimeDiagnostics';
import { ImagePrepareError, photoPipelineDiagnostic, prepareDesignAsset, type PhotoSourceReadDiagnostic } from './editor/image';
import { openTryonOnce } from './analytics/tryonFlow';
import { prepareB2BSession } from './api/b2bSession';
import { saveImageFile } from './utils/download';
import { usePreloadedBlob } from './hooks/usePreloadedBlob';
import { utcTimestamp } from './utils/studioDate';
import { CapabilityProvider } from './features/capabilities/Capability';
import { capabilityIsEnabled } from '../shared/features';
import { bookingArtistAssignment, bookingEntryRoute, defaultActiveLocation } from './features/booking/capabilityFlow';
import { deleteLocalPhoto, pruneLocalPhotos, type LocalPhotoReference } from './storage/photos';
import { logPhotoFlow } from './diagnostics/photoFlow';

const OFFLINE_BOOTSTRAP: BootstrapResponse = { appMode: 'demo', verified: false, role: 'customer', user: null, studio: null, settings: null, locale: 'en', preferredLocale: null, storage: 'local' };

function attribution(): { source: string; campaign: string } {
  const params = new URLSearchParams(location.search);
  const start = telegram.startParam() || params.get('startapp') || '';
  const known = ['tryon', 'quiz', 'full', 'b2b'];
  return { source: params.get('source') || (known.includes(start) ? start : 'direct'), campaign: params.get('campaign') || (!known.includes(start) ? start : '') };
}

export default function App() {
  const { setLocale, t } = useI18n();
  const query = useMemo(() => new URLSearchParams(location.search), []);
  const initialSessionId = query.get('sessionId') || sessionStorage.getItem('walldSessionId') || '';
  const initialPhotoId = sessionStorage.getItem('walldCurrentPhotoId') || '';
  const flowId = useRef(initialSessionId || crypto.randomUUID());
  const sessionIdRef = useRef(initialSessionId);
  const editorRef = useRef<CanvasEngine | null>(null);
  const editorStateRef = useRef<EditorState | null>(null);
  const objectUrls = useRef(new Set<string>());
  const previewUrlsRef = useRef<Record<string, string>>({});
  const routed = useRef(false);
  const resultGeneration = useRef(0);
  const resultGenerating = useRef(false);
  const resultRetryAction = useRef<'save' | 'booking'>('save');
  const resultPersistPromise = useRef<Promise<boolean> | null>(null);
  const resultSaveQueue = useRef(new Map<string, Promise<boolean>>());
  const resultDesignRef = useRef<TattooDesign | null>(null);
  const resultDownloadInFlight = useRef(false);
  const savedDownloadInFlight = useRef(false);
  const openedTryonId = useRef('');
  const designPrepare = useRef(new DesignSelectionGuard());
  const photoIdRef = useRef(initialPhotoId);

  const [bootstrap, setBootstrap] = useState<BootstrapResponse | null>(null);
  const [routes, setRoutes] = useState<AppRoute[]>([{ name: 'home' }]);
  const route = routes[routes.length - 1];
  const routeRef = useRef(route.name);
  const previousRouteName = useRef(route.name);
  routeRef.current = route.name;
  const [photoId, setPhotoId] = useState(initialPhotoId);
  const [design, setDesign] = useState<TattooDesign | null>(null);
  const [designs, setDesigns] = useState<TattooDesign[]>(tattooDesigns);
  const [designCursor, setDesignCursor] = useState<string | null>(null);
  const [designLoading, setDesignLoading] = useState(false);
  const [artists, setArtists] = useState<Artist[]>([]);
  const [sessionId, setSessionId] = useState(initialSessionId);
  const [cleanBlob, setCleanBlob] = useState<Blob | null>(null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [thumbnailBlob, setThumbnailBlob] = useState<Blob | null>(null);
  const [resultPreviewUrl, setResultPreviewUrl] = useState('');
  const [resultPreparing, setResultPreparing] = useState(false);
  const [resultPrepareError, setResultPrepareError] = useState('');
  const [resultBusy, setResultBusy] = useState(false);
  const [resultDownloading, setResultDownloading] = useState(false);
  const [savedDownloading, setSavedDownloading] = useState(false);
  const [resultError, setResultError] = useState('');
  const [resultServerSaved, setResultServerSaved] = useState(false);
  const [resultCloudSaved, setResultCloudSaved] = useState(false);
  const [resultSyncState, setResultSyncState] = useState<TryonSyncState>('local_only');
  const [exportUnlocked, setExportUnlocked] = useState(featureFlags.exportGateMode === 'none');
  const [localTryons, setLocalTryons] = useState<StoredResult[]>([]);
  const [cloudTryons, setCloudTryons] = useState<SavedTryon[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [booking, setBooking] = useState<BookingDraft | null>(null);
  const [bookingBusy, setBookingBusy] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [adminPreview, setAdminPreview] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [photoPrepareError, setPhotoPrepareError] = useState('');

  const rememberSessionId = useCallback((id: string) => {
    sessionIdRef.current = id;
    setSessionId(id);
    sessionStorage.setItem('walldSessionId', id);
  }, []);

  useEffect(() => installRuntimeDiagnostics({
    route: () => routeRef.current,
    report: (metadata) => track('client_runtime_error', sessionIdRef.current || flowId.current, {
      eventType: metadata.eventType,
      route: metadata.route,
      errorName: metadata.errorName,
      code: metadata.code,
      clientRuntime: metadata.clientRuntime,
      ...(metadata.clientVersion ? { clientVersion: metadata.clientVersion } : {}),
    }),
  }), []);

  useEffect(() => () => designPrepare.current.cancel(), []);

  useEffect(() => {
    const activePhotoId = photoIdRef.current;
    void pruneLocalPhotos({ preserveIds: activePhotoId ? [activePhotoId] : [] }).catch(() => undefined);
  }, []);

  const registerUrl = useCallback((blob: Blob) => {
    const url = URL.createObjectURL(blob); objectUrls.current.add(url); return url;
  }, []);
  const revokeRegisteredUrl = useCallback((url: string) => {
    if (!objectUrls.current.delete(url)) return;
    URL.revokeObjectURL(url);
  }, []);

  const storageScope = useMemo<StorageScope | null>(() => bootstrap?.verified && bootstrap.user && bootstrap.studio
    ? { studioId: bootstrap.studio.id, telegramUserId: bootstrap.user.telegramUserId } : null, [bootstrap]);
  const tryonDetailId = route.name === 'tryon' ? route.id || '' : '';
  const tryonDetailLocal = tryonDetailId ? localTryons.find((item) => item.sessionId === tryonDetailId) : undefined;
  const tryonDetailCloud = tryonDetailId ? cloudTryons.find((item) => item.id === tryonDetailId) : undefined;
  const tryonDetailDownload = usePreloadedBlob({
    assetKey: tryonDetailId,
    enabled: Boolean(tryonDetailId && (tryonDetailLocal?.clean || tryonDetailCloud?.fullAvailable)),
    initial: tryonDetailLocal?.clean,
    load: () => downloadTryon(tryonDetailId),
  });

  const refreshHistory = useCallback(async (value: BootstrapResponse | null) => {
    setHistoryLoading(true);
    const scope = value?.verified && value.user && value.studio ? { studioId: value.studio.id, telegramUserId: value.user.telegramUserId } : null;
    const local = scope ? await listStoredResults(scope).catch(() => []) : [];
    setLocalTryons(local);
    const nextUrls: Record<string, string> = {};
    local.forEach((item) => { nextUrls[item.sessionId] = previewUrlsRef.current[item.sessionId] || registerUrl(item.preview); });
    let cloud: SavedTryon[] = [];
    if (value?.verified) cloud = await getSavedTryons().catch(() => []);
    setCloudTryons(cloud);
    await Promise.all(cloud.filter((item) => item.previewAvailable && !nextUrls[item.id]).map(async (item) => {
      if (previewUrlsRef.current[item.id]) { nextUrls[item.id] = previewUrlsRef.current[item.id]; return; }
      try { nextUrls[item.id] = registerUrl(await getTryonPreview(item.id)); } catch { /* metadata remains usable */ }
    }));
    for (const [id, url] of Object.entries(previewUrlsRef.current)) if (nextUrls[id] !== url) revokeRegisteredUrl(url);
    previewUrlsRef.current = nextUrls;
    setPreviewUrls(nextUrls);
    setHistoryLoading(false);
  }, [registerUrl, revokeRegisteredUrl]);

  useEffect(() => {
    const urls = objectUrls;
    let active = true;
    const coldStart = performance.now();
    void telegram.awaitReady().then(async (clientAuth) => {
      track('miniapp_open', flowId.current, { source: attribution().source });
      let networkFailed = false;
      const value = await getBootstrap().catch(() => { networkFailed = true; return OFFLINE_BOOTSTRAP; });
      if (!active) return;
      const authReason = value.verified ? 'verified' : clientAuth.reason === 'init_data_present' ? value.authReason || 'unclassified' : clientAuth.reason;
      document.documentElement.dataset.telegramAuth = authReason;
      console.debug('[WALLD auth]', { authReason, verified: value.verified, platform: clientAuth.platform, initDataPresent: clientAuth.initDataPresent,
        membershipStatus: value.membershipStatus || 'unknown', networkFailed });
      setBootstrap(value);
      telegram.resyncViewport();
      console.debug('[WALLD timing]', { flow: 'cold_start', stage: 'bootstrap_ready', durationMs: Math.round(performance.now() - coldStart), outcome: networkFailed ? 'offline_fallback' : 'ok' });
      if (value.verified || !localLocalePreference()) setLocale(value.locale);
      if (!routed.current) {
        routed.current = true;
        const landing = roleLanding(value.role, value.appMode);
        const deep = resolveDeepRoute(location.search, telegram.startParam() || query.get('startapp') || '', value.role, value.appMode);
        setRoutes(deep.name === landing.name && deep.id === landing.id ? [deep] : [landing, deep]);
      }
      void refreshHistory(value);
      if (capabilityIsEnabled(value.capabilities, 'editor.basic')) void getDesigns({ limit: 24 }).then((result) => { if (!active) return; setDesigns(result.designs); setDesignCursor(result.nextCursor); }).catch(() => undefined);
      if (capabilityIsEnabled(value.capabilities, 'artists.enabled')) void getArtists().then((items) => { if (active) setArtists(items); }).catch(() => undefined);
    });
    return () => { active = false; urls.current.forEach((url) => URL.revokeObjectURL(url)); urls.current.clear(); };
  }, [query, refreshHistory, setLocale]);

  const navigate = useCallback((next: AppRoute) => setRoutes((current) => [...current, next]), []);
  const redirectHome = useCallback((reason: string) => {
    logPhotoFlow('TRYON_REDIRECT', { destination: 'home' });
    logPhotoFlow('TRYON_REDIRECT_REASON', { reason });
    setRoutes([{ name: 'home' }]);
  }, []);
  const back = useCallback(() => {
    resultGeneration.current += 1; resultGenerating.current = false; setResultPreparing(false); setResultPrepareError('');
    setRoutes((current) => current.length > 1 ? current.slice(0, -1) : current);
  }, []);
  const releaseTryonMemory = useCallback(() => {
    designPrepare.current.cancel();
    resultGeneration.current += 1; resultGenerating.current = false; setResultPreparing(false); setResultPrepareError('');
    editorRef.current = null;
    editorStateRef.current = null;
    const releasedPhotoId = photoIdRef.current;
    photoIdRef.current = '';
    sessionStorage.removeItem('walldCurrentPhotoId');
    setPhotoId('');
    if (releasedPhotoId) void deleteLocalPhoto(releasedPhotoId).catch(() => undefined);
    setDesign(null);
    resultDesignRef.current = null;
    setCleanBlob(null);
    setPreviewBlob(null);
    setThumbnailBlob(null);
    setResultPreviewUrl((current) => { if (current) URL.revokeObjectURL(current); return ''; });
  }, []);
  const resetHome = useCallback((reason = 'user_reset') => {
    openedTryonId.current = '';
    setPhotoPrepareError('');
    releaseTryonMemory();
    redirectHome(reason);
  }, [redirectHome, releaseTryonMemory]);
  const handleEditorReady = useCallback((engine: CanvasEngine) => {
    editorRef.current = engine;
    track('editor_started', sessionIdRef.current || flowId.current, { designId: design?.id || '', designName: design?.name || '' });
  }, [design]);
  const handleEditorDispose = useCallback((engine: CanvasEngine) => { if (editorRef.current === engine) editorRef.current = null; }, []);

  useEffect(() => {
    if (routes.length <= 1) return;
    return telegram.showBack(back);
  }, [back, routes.length]);

  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); }, [route.id, route.name]);

  useEffect(() => {
    if (route.name === 'home' && previousRouteName.current !== 'home') openedTryonId.current = '';
    previousRouteName.current = route.name;
  }, [route.name]);

  const homeTryons = useMemo<HomeTryon[]>(() => {
    const merged = new Map<string, HomeTryon>();
    const defaultTimezone = bootstrap?.settings?.locations?.find((item) => item.default)?.timezone
      || bootstrap?.settings?.locations?.[0]?.timezone || bootstrap?.studio?.timezone || 'UTC';
    cloudTryons.forEach((item) => merged.set(item.id, { id: item.id, designName: item.designName, designCategory: item.designCategory,
      previewUrl: previewUrls[item.id] || '', bookingStatus: item.bookingStatus, createdAt: utcTimestamp(item.createdAt), businessTimezone: item.businessTimezone || defaultTimezone }));
    localTryons.forEach((item) => merged.set(item.sessionId, { id: item.sessionId, designName: item.designName || t('app.savedTryon'),
      designCategory: item.designCategory || '', previewUrl: previewUrls[item.sessionId] || '',
      bookingStatus: merged.get(item.sessionId)?.bookingStatus || item.bookingStatus || 'not_requested', createdAt: merged.get(item.sessionId)?.createdAt || item.createdAt,
      businessTimezone: merged.get(item.sessionId)?.businessTimezone || defaultTimezone }));
    return sortHomeTryonsNewestFirst([...merged.values()]);
  }, [bootstrap, cloudTryons, localTryons, previewUrls, t]);

  const editorEnabled = capabilityIsEnabled(bootstrap?.capabilities, 'editor.basic');
  const bookingEnabled = capabilityIsEnabled(bootstrap?.capabilities, 'booking.enabled');
  const artistSelectionEnabled = capabilityIsEnabled(bootstrap?.capabilities, 'booking.artist_selection');
  const locationSelectionEnabled = capabilityIsEnabled(bootstrap?.capabilities, 'booking.location_selection');
  const configuredLocations = useMemo(() => bootstrap?.settings?.locations || [], [bootstrap?.settings?.locations]);
  const defaultLocation = useMemo(() => defaultActiveLocation(configuredLocations), [configuredLocations]);
  const activeArtists = useMemo(() => artists.filter((item) => item.active), [artists]);
  const automaticArtistAssignment = useMemo(() => bookingArtistAssignment(artists, artistSelectionEnabled), [artistSelectionEnabled, artists]);
  const automaticArtist = automaticArtistAssignment.artist;
  const nextBookingEntry = useCallback(() => bookingEntryRoute({ locationSelectionEnabled, artistSelectionEnabled, locations: configuredLocations }), [artistSelectionEnabled, configuredLocations, locationSelectionEnabled]);

  useEffect(() => {
    if (!bootstrap || bootstrap.role !== 'customer') return;
    if (!editorEnabled && ['design', 'editor', 'result'].includes(route.name)) { redirectHome('editor_feature_unavailable'); return; }
    if (!bookingEnabled && route.name.startsWith('booking-')) { redirectHome('booking_feature_unavailable'); return; }
    if (!booking) return;
    if ((!locationSelectionEnabled || configuredLocations.filter((item) => item.active).length <= 1) && route.name === 'booking-location') {
      setBooking((current) => current && defaultLocation ? { ...current, locationId: defaultLocation.id, locationName: defaultLocation.name } : current);
      setRoutes((current) => [...current.slice(0, -1), { name: artistSelectionEnabled ? 'artist-select' : 'booking-date' }]);
    } else if (!artistSelectionEnabled && (route.name === 'artist-select' || route.name === 'artist-profile')) {
      setRoutes((current) => [...current.slice(0, -1), { name: 'booking-date' }]);
    }
  }, [artistSelectionEnabled, booking, bookingEnabled, bootstrap, configuredLocations, defaultLocation, editorEnabled, locationSelectionEnabled, redirectHome, route.name]);

  const loadMoreDesigns = async () => {
    if (!designCursor || designLoading) return;
    setDesignLoading(true);
    try { const result = await getDesigns({ cursor: designCursor, limit: 24 }); setDesigns((current) => [...current, ...result.designs]); setDesignCursor(result.nextCursor); }
    finally { setDesignLoading(false); }
  };

  const beginTryon = useCallback(() => {
    setPhotoPrepareError('');
    if (!capabilityIsEnabled(bootstrap?.capabilities, 'editor.basic')) return sessionIdRef.current || flowId.current;
    return openTryonOnce(openedTryonId, (id) => {
      flowId.current = id;
      rememberSessionId(id);
      track('tryon_opened', id);
    });
  }, [bootstrap?.capabilities, rememberSessionId]);

  const reportImagePrepareError = useCallback((cause: unknown, file: File | null) => {
    const id = openedTryonId.current || sessionIdRef.current || flowId.current;
    const runtime = clientRuntime(navigator.userAgent);
    const fallback = cause instanceof ImagePrepareError
      ? { code: cause.code, stage: cause.stage, errorClass: cause.name }
      : { code: 'photo_restore_failed', stage: 'photo_load', errorClass: cause instanceof Error ? cause.name : typeof cause };
    track('image_prepare_failed', id, { ...(file ? photoPipelineDiagnostic(cause, file) : fallback), photoId: photoIdRef.current, ...runtime });
  }, []);

  const choosePhoto = (photo: LocalPhotoReference) => {
    if (!editorEnabled) return;
    designPrepare.current.cancel();
    const nextSession = openedTryonId.current || beginTryon();
    flowId.current = nextSession; rememberSessionId(nextSession);
    editorStateRef.current = null;
    const previousPhotoId = photoIdRef.current;
    photoIdRef.current = photo.id;
    sessionStorage.setItem('walldCurrentPhotoId', photo.id);
    setPhotoId(photo.id);
    if (previousPhotoId && previousPhotoId !== photo.id) void deleteLocalPhoto(previousPhotoId).catch(() => undefined);
    setCleanBlob(null); setPreviewBlob(null); setThumbnailBlob(null); setResultServerSaved(false); setResultCloudSaved(false); setExportUnlocked(featureFlags.exportGateMode === 'none'); setResultError('');
    resultDesignRef.current = null; setDesign(null); navigate({ name: 'design' });
    track('tryon_started', nextSession); track('photo_uploaded', nextSession, { type: photo.mime, bytes: photo.bytes }); track('design_library_opened', nextSession);
  };

  const handleEditorPrepareError = useCallback((cause: unknown, failedPhoto: File | null) => {
    reportImagePrepareError(cause, failedPhoto);
    telegram.haptic('error');
  }, [reportImagePrepareError]);

  const handleEditorPhotoChange = useCallback((nextPhoto: LocalPhotoReference) => {
    const previousPhotoId = photoIdRef.current;
    photoIdRef.current = nextPhoto.id;
    sessionStorage.setItem('walldCurrentPhotoId', nextPhoto.id);
    setPhotoId(nextPhoto.id);
    if (previousPhotoId && previousPhotoId !== nextPhoto.id) void deleteLocalPhoto(previousPhotoId).catch(() => undefined);
  }, []);

  const reportPhotoReadRetrySuccess = useCallback((diagnostic: PhotoSourceReadDiagnostic) => {
    const id = openedTryonId.current || sessionIdRef.current || flowId.current;
    track('photo_read_retry_success', id, {
      stage: diagnostic.stage, attempt: diagnostic.attempt, mime: diagnostic.mime, bytes: diagnostic.bytes,
      errorClass: diagnostic.errorClass, ...clientRuntime(navigator.userAgent),
    });
  }, []);

  const reportDesignPrepareError = useCallback((cause: unknown, designId: string) => {
    const runtime = clientRuntime(navigator.userAgent);
    track('design_asset_failed', sessionIdRef.current || flowId.current, {
      ...designAssetDiagnostic(cause, designId),
      platform: telegram.platform(),
      ...runtime,
    });
  }, []);

  const chooseDesign = async (next: TattooDesign, custom = false): Promise<void> => {
    const operation = designPrepare.current.begin();
    const started = performance.now();
    try {
      await prepareDesignAsset(next.asset);
      designPrepare.current.assertCurrent(operation);
      if (routeRef.current !== 'design') throw staleDesignAssetError();
      editorStateRef.current = null;
      const selected = { ...next, ...(custom ? { id: next.id } : {}) };
      resultDesignRef.current = selected;
      setDesign(selected);
      logPhotoFlow('DESIGN_SELECTED', { designId: next.id, photoId: photoIdRef.current });
      navigate({ name: 'editor' });
      track(custom ? 'custom_design_uploaded' : 'design_selected', flowId.current, { designId: next.id, designName: next.name, category: next.category });
      telegram.haptic('selection');
      console.debug('[WALLD timing]', { flow: 'design_to_editor', stage: 'asset_ready', durationMs: Math.round(performance.now() - started), outcome: 'ok' });
    } catch (cause) {
      const error = !designPrepare.current.isCurrent(operation) ? staleDesignAssetError() : asDesignAssetError(cause);
      reportDesignPrepareError(error, next.id);
      if (!isDesignAssetCancellation(error)) invalidateCatalogAsset(next.asset);
      console.debug('[WALLD timing]', { flow: 'design_to_editor', stage: error.stage, durationMs: Math.round(performance.now() - started), outcome: error.code });
      throw error;
    }
  };
  const handleCustom = async (custom: CustomDesign): Promise<void> => chooseDesign(custom.design, true);
  const handleEditorDesignChange = useCallback((next: TattooDesign, custom: boolean) => {
    resultDesignRef.current = next;
    setDesign(next);
    track(custom ? 'custom_design_uploaded' : 'design_selected', sessionIdRef.current || flowId.current, { designId: next.id, designName: next.name, category: custom ? '' : next.category });
  }, []);

  const persistResult = useCallback(async (value: { id: string; clean: Blob; preview: Blob; thumbnail: Blob; design: TattooDesign }): Promise<boolean> => {
    const generation = resultGeneration.current;
    const isCurrent = () => generation === resultGeneration.current;
    if (!storageScope) {
      if (!bootstrap?.verified) setResultSyncState('local_only');
      return false;
    }
    const localValue = { sessionId: value.id, clean: value.clean, preview: value.preview, createdAt: Date.now(), designId: value.design.id,
      designName: value.design.name, designCategory: value.design.category, custom: value.design.id.startsWith('custom-'), cloudSaved: false, bookingStatus: 'not_requested' } as const;
    const localSaved = storeResult(storageScope, localValue).catch(() => undefined);
    if (!bootstrap?.verified) return false;
    const key = `${storageScope.studioId}:${storageScope.telegramUserId}:${value.id}`;
    const previous = resultSaveQueue.current.get(key);
    // Save locally immediately, but serialize remote revisions of the same Try-On.
    // An older slow B2 upload must never overwrite a newer edited composition.
    const pending = (async () => {
      await localSaved;
      await previous?.catch(() => false);
      try {
        await createTryonSession({ sessionId: value.id, ...attribution() });
        const metadata = await saveTryon({ id: value.id, sessionId: value.id, designId: value.design.id, designName: value.design.name,
          designCategory: value.design.category, custom: value.design.id.startsWith('custom-') });
        if (isCurrent()) setResultServerSaved(true);
        if (!metadata.uploadAvailable) {
          if (isCurrent()) {
            setResultCloudSaved(false);
            setResultError(t('api.storageTemporary')); setResultSyncState('sync_failed');
          }
          await refreshHistory(bootstrap);
          return true;
        }
        try {
          const syncState = await uploadTryonAssets(value.id, value.clean, value.thumbnail);
          if (isCurrent()) {
            await updateStoredResult(storageScope, value.id, { cloudSaved: syncState === 'synced' }).catch(() => undefined);
            setResultCloudSaved(syncState === 'synced'); setResultSyncState(syncState); setResultError('');
          }
          track('booking_saved', value.id);
          await refreshHistory(bootstrap);
        } catch (cause) {
          if (isCurrent()) {
            setResultCloudSaved(false); setResultSyncState('sync_failed');
            setResultError(cause instanceof Error ? cause.message : t('app.saveTryonError'));
            telegram.haptic('error');
          }
          await refreshHistory(bootstrap);
        }
        return true;
      } catch (cause) {
        if (isCurrent()) {
          setResultServerSaved(false);
          setResultCloudSaved(false); setResultSyncState('sync_failed');
          setResultError(cause instanceof Error ? cause.message : t('app.saveTryonError'));
          telegram.haptic('error');
        }
        await refreshHistory(bootstrap);
        return false;
      }
    })();
    resultSaveQueue.current.set(key, pending);
    const clear = () => { if (resultSaveQueue.current.get(key) === pending) resultSaveQueue.current.delete(key); };
    void pending.then(clear, clear);
    return pending;
  }, [bootstrap, refreshHistory, storageScope, t]);

  const showResult = useCallback(async (engine: CanvasEngine, selectedDesign?: TattooDesign) => {
    if (resultGenerating.current) return;
    const operation = ++resultGeneration.current;
    resultGenerating.current = true;
    setResultPreparing(true); setResultPrepareError(''); setResultError(''); setResultBusy(false);
    if (routeRef.current !== 'result') navigate({ name: 'result' });
    try {
      const finalDesign = selectedDesign || resultDesignRef.current || design;
      if (!finalDesign) throw new Error('missing-design');
      resultDesignRef.current = finalDesign;
      setDesign(finalDesign);
      editorStateRef.current = engine.getState();
      // Keep the owning editor mounted until export settles, including a retry after failure.
      // Yield a frame so the preparing screen paints before synchronous canvas drawing.
      await new Promise<void>((resolve) => requestAnimationFrame(() => { setTimeout(resolve, 0); }));
      if (operation !== resultGeneration.current) return;
      const [clean, preview, thumbnail] = await Promise.all([
        engine.exportBlob({ watermark: false }),
        engine.exportBlob({ watermark: true, maxEdge: 1600, quality: .9 }),
        engine.exportBlob({ watermark: true, maxEdge: 480, quality: .82 }),
      ]);
      if (operation !== resultGeneration.current) return;
      const id = sessionIdRef.current || flowId.current || crypto.randomUUID(); rememberSessionId(id);
      setCleanBlob(clean); setPreviewBlob(preview); setThumbnailBlob(thumbnail);
      setResultServerSaved(false); setResultCloudSaved(false); setResultSyncState(bootstrap?.verified ? 'syncing' : 'local_only');
      setResultPreviewUrl((current) => { if (current) URL.revokeObjectURL(current); return URL.createObjectURL(preview); });
      track('result_viewed', id); track('tryon_result_created', id, { tryonId: id });
      if (bootstrap?.verified) setExportUnlocked(featureFlags.exportGateMode === 'none');
      // Local generation unlocks the UI; persistence and B2 sync have their own state.
      const pending = persistResult({ id, clean, preview, thumbnail, design: finalDesign });
      resultPersistPromise.current = pending;
      const clear = () => { if (resultPersistPromise.current === pending) resultPersistPromise.current = null; };
      void pending.then(clear, clear);
      telegram.haptic('success');
    } catch {
      if (operation === resultGeneration.current) { setResultPrepareError(t('result.prepareError')); telegram.haptic('error'); }
    } finally {
      if (operation === resultGeneration.current) { resultGenerating.current = false; setResultPreparing(false); }
    }
  }, [bootstrap?.verified, design, navigate, persistResult, rememberSessionId, t]);

  const ensureCurrentSaved = async (): Promise<boolean> => {
    if (resultCloudSaved) return true;
    if (!bootstrap?.verified) { setResultError(t('app.openTelegram')); return false; }
    if (!cleanBlob || !previewBlob || !thumbnailBlob || !design || !sessionId || !storageScope) { setResultError(t('app.finishTryon')); return false; }
    setResultBusy(true); setResultError(''); setResultSyncState('syncing');
    try {
      return await persistResult({ id: sessionId, clean: cleanBlob, preview: previewBlob, thumbnail: thumbnailBlob, design });
    } catch (cause) { setResultError(cause instanceof Error ? cause.message : t('app.saveTryonError')); return false; }
    finally { setResultBusy(false); }
  };

  const buildDraft = useCallback(async (id: string): Promise<BookingDraft | null> => {
    const local = localTryons.find((item) => item.sessionId === id) || (storageScope ? await getStoredResult(storageScope, id).catch(() => null) : null);
    const cloud = cloudTryons.find((item) => item.id === id);
    const selectedDesign = designs.find((item) => item.id === (local?.designId || cloud?.designId));
    const designName = local?.designName || cloud?.designName;
    if (!designName) return null;
    const location = !locationSelectionEnabled || configuredLocations.filter((item) => item.active).length <= 1 ? defaultLocation : null;
    return { tryonId: id, designId: local?.designId || cloud?.designId || null, designName, designCategory: local?.designCategory || cloud?.designCategory || '',
      designTags: selectedDesign?.tags || [], custom: local?.custom || cloud?.custom || false, previewUrl: previewUrls[id] || '', artistId: automaticArtistAssignment.artistId,
      artistPreference: automaticArtistAssignment.artistPreference, locationId: location?.id || null,
      locationName: location?.name || '', preferredDate: '', preferredStartTime: '', clientRequestId: crypto.randomUUID() };
  }, [automaticArtistAssignment, cloudTryons, configuredLocations, defaultLocation, designs, localTryons, locationSelectionEnabled, previewUrls, storageScope]);

  const startBooking = async (id = sessionId) => {
    setBookingError('');
    if (!bookingEnabled) return;
    track('booking_started', id || flowId.current, { tryonId: id || flowId.current });
    let serverSaved = resultServerSaved;
    if (id === sessionId && resultPersistPromise.current) serverSaved = await resultPersistPromise.current;
    if (id === sessionId && !serverSaved && previewBlob) { if (!await ensureCurrentSaved()) { resultRetryAction.current = 'booking'; return; } }
    const location = !locationSelectionEnabled || configuredLocations.filter((item) => item.active).length <= 1 ? defaultLocation : null;
    const draft = id === sessionId && design ? { tryonId: id, designId: design.id, designName: design.name, designCategory: design.category,
      designTags: design.tags || [], custom: design.id.startsWith('custom-'), previewUrl: resultPreviewUrl || previewUrls[id] || '', artistId: automaticArtistAssignment.artistId,
      artistPreference: automaticArtistAssignment.artistPreference, locationId: location?.id || null,
      locationName: location?.name || '', preferredDate: '', preferredStartTime: '', clientRequestId: crypto.randomUUID() } : await buildDraft(id);
    if (!draft) { setResultError(t('app.openSavedError')); return; }
    setBooking(draft); navigate({ name: nextBookingEntry() }); track('booking_continue_clicked', id);
  };

  const openTryon = async (id: string) => { const draft = await buildDraft(id); if (draft) { setBooking(draft); navigate({ name: 'tryon', id }); } };
  const deleteTryon = async (id: string) => {
    setDeleteBusy(true);
    await Promise.allSettled([...(storageScope ? [deleteStoredResult(storageScope, id)] : []), ...(bootstrap?.verified ? [deleteSavedTryon(id)] : [])]); track('tryon_deleted', id); await refreshHistory(bootstrap);
    setDeleteBusy(false); setPendingDeleteId(null); if (route.name === 'tryon') resetHome('saved_tryon_deleted');
  };

  const submitBooking = async () => {
    if (!booking) return; setBookingBusy(true); setBookingError('');
    const started = performance.now();
    console.debug('[WALLD timing]', { flow: 'booking_submit', stage: 'pending_ui', durationMs: 0, outcome: 'ok' });
    try {
      await createBookingRequest({ tryonId: booking.tryonId, clientRequestId: booking.clientRequestId, artistId: booking.artistId, artistPreference: booking.artistPreference,
        preferredDate: booking.preferredDate, preferredStartTime: booking.preferredStartTime, locationId: booking.locationId, ...attribution() });
      const responseMs = Math.round(performance.now() - started);
      console.debug('[WALLD timing]', { flow: 'booking_submit', stage: 'worker_response', durationMs: responseMs, outcome: 'ok' });
      setExportUnlocked(true);
      track('booking_submitted', booking.tryonId); telegram.haptic('success'); navigate({ name: 'booking-success' });
      console.debug('[WALLD timing]', { flow: 'booking_submit', stage: 'success_route', durationMs: Math.round(performance.now() - started), outcome: 'ok' });
      void (async () => {
        if (storageScope) await updateStoredResult(storageScope, booking.tryonId, { bookingStatus: 'new' }).catch(() => undefined);
        await refreshHistory(bootstrap).catch(() => undefined);
      })();
    } catch (cause) {
      console.debug('[WALLD timing]', { flow: 'booking_submit', stage: 'worker_response', durationMs: Math.round(performance.now() - started), outcome: 'failed' });
      setBookingError(cause instanceof Error ? cause.message : t('app.bookingSendError')); telegram.haptic('error');
    }
    finally { setBookingBusy(false); }
  };

  const downloadSavedTryon = async (id: string, name: string) => {
    if (savedDownloadInFlight.current) return;
    savedDownloadInFlight.current = true;
    const started = performance.now();
    setSavedDownloading(true);
    console.debug('[WALLD timing]', { flow: 'download', context: 'saved_tryon', stage: 'pending_ui', durationMs: 0, outcome: 'ok' });
    setResultError('');
    if (id !== tryonDetailId || !tryonDetailDownload.blob) { savedDownloadInFlight.current = false; setSavedDownloading(false); return; }
    try {
      await saveImageFile(tryonDetailDownload.blob, `walld-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 48) || id.slice(0, 8)}.jpg`, {
        platform: telegram.platform(), operationKey: `saved:${id}`, onEvent: (event, metadata) => track(event, id, metadata),
        onTiming: (stage, elapsedMs) => console.debug('[WALLD timing]', { flow: 'download', context: 'saved_tryon', stage, durationMs: Math.round(elapsedMs), totalMs: Math.round(performance.now() - started), outcome: 'ok' }),
        ...(telegram.supportsNativeDownload() ? { nativeUrl: () => createTryonDownloadTicket(id).then((ticket) => ({ url: ticket.url, fileName: ticket.fileName })), nativeDownload: telegram.downloadFile } : {}),
      });
      track('result_downloaded', id, { tryonId: id });
    } catch (cause) {
      if (!(cause instanceof DOMException && cause.name === 'AbortError')) setResultError(t('app.downloadError'));
    }
    finally { savedDownloadInFlight.current = false; setSavedDownloading(false); }
  };

  const downloadCurrentResult = async () => {
    if (!cleanBlob || resultDownloadInFlight.current) return;
    resultDownloadInFlight.current = true;
    const started = performance.now();
    setResultDownloading(true); setResultError('');
    console.debug('[WALLD timing]', { flow: 'download', context: 'result', stage: 'pending_ui', durationMs: 0, outcome: 'ok' });
    try {
      await saveImageFile(cleanBlob, `walld-${sessionId.slice(0, 8)}.jpg`, {
        platform: telegram.platform(), operationKey: `result:${sessionId}`, onEvent: (event, metadata) => track(event, sessionId, metadata),
        onTiming: (stage, elapsedMs) => console.debug('[WALLD timing]', { flow: 'download', context: 'result', stage, durationMs: Math.round(elapsedMs), totalMs: Math.round(performance.now() - started), outcome: 'ok' }),
        ...(telegram.supportsNativeDownload() && resultCloudSaved
          ? { nativeUrl: () => createTryonDownloadTicket(sessionId).then((ticket) => ({ url: ticket.url, fileName: ticket.fileName })), nativeDownload: telegram.downloadFile }
          : {}),
      });
      track('result_downloaded', sessionId, { tryonId: sessionId });
    } catch (cause) {
      if (!(cause instanceof DOMException && cause.name === 'AbortError')) setResultError(t('app.downloadError'));
    }
    finally { resultDownloadInFlight.current = false; setResultDownloading(false); }
  };

  const prepareB2B = useCallback(async () => {
    const id = sessionIdRef.current || flowId.current;
    rememberSessionId(id);
    try {
      await prepareB2BSession({
        sessionId: id,
        request: (signal) => createTryonSession({ sessionId: id, ...attribution(), tryonCompleted: false }, { signal }),
        onTiming: (timing) => {
          console.debug('[WALLD timing]', { flow: 'b2b', ...timing });
          track('b2b_prepare_timing', id, { stage: timing.stage, duration_ms: timing.durationMs, error_code: timing.errorCode, attempt: timing.attempt });
        },
      });
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') throw new Error('b2b_prepare_timeout');
      throw cause;
    }
  }, [rememberSessionId]);

  const switchAdmin = (tab: AdminTab) => { releaseTryonMemory(); setRoutes([{ name: `admin-${tab}` as AppRoute['name'] }]); };
  const currentArtist = route.name === 'artist-profile' ? artists.find((item) => item.id === route.id) : null;
  const selectedArtist = booking?.artistId ? artists.find((item) => item.id === booking.artistId) || null : automaticArtist;
  const bookingLocations = configuredLocations;
  const selectedLocation = booking?.locationId ? bookingLocations.find((item) => item.id === booking.locationId) : undefined;

  if (!bootstrap) return <main className="screen state-screen"><span className="spinner" /><h1>{t('app.opening')}</h1></main>;
  if (!canOpenAdmin(route, bootstrap.role)) return <main className="screen state-screen"><h1>{t('app.accessRequired')}</h1><p>{t('app.accessCopy')}</p></main>;
  if (bootstrap.role === 'customer' && bootstrap.subscription?.consumerAvailable === false) return <main className="screen state-screen"><h1>WALLD Studio</h1><p>{t('api.serviceUnavailable')}</p></main>;

  const adminTab = route.name === 'admin-lead' ? 'leads' : route.name.startsWith('admin-') && route.name !== 'admin-request' ? route.name.replace('admin-', '') as AdminTab : 'requests';
  const cleanDownloadCapabilityEnabled = capabilityIsEnabled(bootstrap.capabilities, 'downloads.clean');
  const cleanDownloadEnabled = bootstrap.settings?.booking?.allowCleanDownload !== false && cleanDownloadCapabilityEnabled;
  return <CapabilityProvider capabilities={bootstrap.capabilities}><div className="app-shell">
    {route.name === 'home' && <HomeScreen tryons={homeTryons} historyLoading={historyLoading} adminPreview={adminPreview} onReturnAdmin={() => { setAdminPreview(false); switchAdmin('dashboard'); }} onTryonOpen={beginTryon} onPhoto={choosePhoto} onPhotoError={reportImagePrepareError} onPhotoReadRetrySuccess={reportPhotoReadRetrySuccess} externalError={photoPrepareError} onBook={(id) => void startBooking(id)} onView={(id) => void openTryon(id)} onForStudios={() => bootstrap.appMode === 'demo' && (bootstrap.role === 'owner' || bootstrap.role === 'admin') ? switchAdmin('dashboard') : navigate({ name: 'offer' })} onPrivacy={() => navigate({ name: 'privacy' })} enabledLocales={bootstrap.settings?.enabledLocales} preferredLocale={bootstrap.preferredLocale} verified={bootstrap.verified} />}
    {route.name === 'design' && <DesignLibrary designs={designs} hasMore={Boolean(designCursor)} loadingMore={designLoading} onLoadMore={() => void loadMoreDesigns()} onBack={() => { designPrepare.current.cancel(); back(); }} onSelect={chooseDesign} onCustom={handleCustom} />}
    {(route.name === 'editor' || (route.name === 'result' && (resultPreparing || Boolean(resultPrepareError)))) && photoId && design && <div className={`editor-host${route.name === 'result' ? ' is-suspended' : ''}`} aria-hidden={route.name === 'result'}><EditorScreen photoId={photoId} initialDesign={design} initialState={editorStateRef.current} designs={designs} onBack={back} onResult={(engine, selectedDesign) => void showResult(engine, selectedDesign)} onReady={handleEditorReady} onDispose={handleEditorDispose} onPrepareError={handleEditorPrepareError} onPhotoReadRetrySuccess={reportPhotoReadRetrySuccess} onPhotoChange={handleEditorPhotoChange} onDesignPrepareError={reportDesignPrepareError} onDesignChange={handleEditorDesignChange} /></div>}
    {route.name === 'result' && <ResultScreen previewUrl={resultPreviewUrl} preparing={resultPreparing} prepareError={resultPrepareError} onPrepareRetry={() => { if (editorRef.current) void showResult(editorRef.current); }} unlocked={exportUnlocked && cleanDownloadCapabilityEnabled} allowCleanDownload={cleanDownloadEnabled} syncState={resultSyncState} busy={resultBusy} downloadBusy={resultDownloading} error={resultError} onBack={back} onBooking={() => { resultRetryAction.current = 'booking'; setResultBusy(true); void startBooking().finally(() => setResultBusy(false)); }} onRetry={() => resultRetryAction.current === 'booking' ? void startBooking() : void ensureCurrentSaved()} onEdit={() => navigate({ name: 'editor' })} onTryAnother={() => resetHome('user_try_another')} onSaveFile={() => void downloadCurrentResult()} />}
    {route.name === 'tryon' && booking && <TryonDetailScreen draft={booking} allowCleanDownload={cleanDownloadEnabled} downloadReady={Boolean(tryonDetailDownload.blob)} downloadLoading={tryonDetailDownload.loading} downloadBusy={savedDownloading} error={resultError || (tryonDetailDownload.error ? t('app.downloadError') : '')} onDownloadRetry={tryonDetailDownload.retry} onDownload={() => void downloadSavedTryon(booking.tryonId, booking.designName)} onBack={back} onBook={() => { track('booking_started', booking.tryonId, { tryonId: booking.tryonId }); navigate({ name: nextBookingEntry() }); }} onDelete={() => setPendingDeleteId(booking.tryonId)} />}
    {route.name === 'booking-location' && booking && locationSelectionEnabled && <BookingLocationScreen locations={bookingLocations.filter((item) => item.active)} value={booking.locationId} onBack={back} onChoose={(locationId) => { const location = bookingLocations.find((item) => item.id === locationId); setBooking({ ...booking, locationId, locationName: location?.name || '' }); navigate({ name: artistSelectionEnabled ? 'artist-select' : 'booking-date' }); }} />}
    {route.name === 'artist-select' && booking && artistSelectionEnabled && <ArtistSelectScreen draft={booking} artists={activeArtists} onBack={back} onProfile={(id) => navigate({ name: 'artist-profile', id })} onChoose={(id) => { const artist = artists.find((item) => item.id === id); setBooking({ ...booking, artistId: id, artistPreference: id ? 'specific' : 'help_me_choose' }); track('booking_artist_selected', booking.tryonId, { artistId: id || 'help_me_choose', artistName: artist?.name || 'help_me_choose' }); navigate({ name: 'booking-date' }); }} />}
    {route.name === 'artist-profile' && currentArtist && artistSelectionEnabled && <ArtistProfileScreen artist={currentArtist} onBack={back} onChoose={() => { if (booking) { setBooking({ ...booking, artistId: currentArtist.id, artistPreference: 'specific' }); track('booking_artist_selected', booking.tryonId, { artistId: currentArtist.id, artistName: currentArtist.name }); } navigate({ name: 'booking-date' }); }} />}
    {route.name === 'booking-date' && booking && <BookingDateScreen value={booking.preferredDate} artistId={booking.artistId} locationId={booking.locationId} settings={selectedLocation || bootstrap.settings?.booking} onBack={back} onContinue={(preferredDate) => { setBooking({ ...booking, preferredDate }); track('booking_date_selected', booking.tryonId, { date: preferredDate }); navigate({ name: 'booking-time' }); }} />}
    {route.name === 'booking-time' && booking && <BookingTimeScreen value={booking.preferredStartTime} date={booking.preferredDate} artistId={booking.artistId} locationId={booking.locationId} onBack={back} onContinue={(preferredStartTime) => { setBooking({ ...booking, preferredStartTime }); track('booking_time_selected', booking.tryonId, { time: preferredStartTime }); navigate({ name: 'booking-review' }); }} />}
    {route.name === 'booking-review' && booking && <BookingReviewScreen draft={booking} artist={selectedArtist} telegramName={bootstrap.user?.username ? `@${bootstrap.user.username}` : bootstrap.user?.firstName || t('app.verifiedUser')} busy={bookingBusy} error={bookingError} onBack={back} onSubmit={() => void submitBooking()} />}
    {route.name === 'booking-success' && booking && <BookingSuccessScreen date={booking.preferredDate} time={booking.preferredStartTime} onHistory={() => resetHome('booking_history')} onNew={() => resetHome('booking_new_tryon')} />}
    {route.name === 'offer' && <OfferScreen sessionId={sessionId || flowId.current} verified={bootstrap.verified} enabledLocales={bootstrap.settings?.enabledLocales} preferredLocale={bootstrap.preferredLocale} onBack={back} onPrefetch={prepareB2B} onRequest={() => navigate({ name: 'b2b-form' })} />}
    {route.name === 'b2b-form' && bootstrap.verified && <B2BRequestScreen sessionId={sessionId || flowId.current} username={bootstrap.user?.username || ''} firstName={bootstrap.user?.firstName || ''} source={attribution().source} onPrepare={prepareB2B} onBack={back} />}
    {route.name === 'reveal' && <RevealScreen sessionId={route.id || sessionId || flowId.current} onBack={back} onRequest={() => navigate({ name: 'b2b-form' })} onSave={(blob) => { const id = route.id || sessionId || flowId.current; void saveImageFile(blob, `walld-${id.slice(0, 8)}.jpg`, { platform: telegram.platform(), onEvent: (event, metadata) => track(event, id, metadata) }).then(() => track('result_downloaded', id, { tryonId: id })); }} />}
    {route.name === 'privacy' && <PrivacyScreen scope={storageScope} onBack={back} onErased={() => { releaseTryonMemory(); setLocalTryons([]); setCloudTryons([]); }} />}
    {(route.name.startsWith('admin-')) && <StudioAdmin tab={adminTab} requestId={route.name === 'admin-request' ? route.id : undefined} leadId={route.name === 'admin-lead' ? route.id : undefined} bootstrap={bootstrap} onTab={switchAdmin} onRequest={(id) => navigate({ name: 'admin-request', id })} onLead={(id) => navigate({ name: 'admin-lead', id })} onCustomerView={() => { setAdminPreview(true); navigate({ name: 'home' }); }} onBack={back} />}
    <ConfirmDialog open={Boolean(pendingDeleteId)} title={t('app.deleteTitle')} copy={t('app.deleteConfirm')} confirmLabel={t('common.delete')} cancelLabel={t('common.cancel')} busy={deleteBusy} onCancel={() => setPendingDeleteId(null)} onConfirm={() => pendingDeleteId && void deleteTryon(pendingDeleteId)} />
  </div></CapabilityProvider>;
}
